import type { SelectChangeEvent } from "@mui/material";
import { Divider, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { selectListFieldsState, selectListLedger } from "../../../../store/selectors/fieldsStateSelectors.ts";
import { selectLedgers } from "../../../../store/selectors/metaDataSelectors.ts";
import { selectEditDisabled } from "../../../../store/selectors/uiSettingsSelectors.ts";
import { fieldsStateActions } from "../../../../store/thunks/fieldStatesThunks.ts";
import { createListLedgerChange } from "../../../../store/utils/listLedgerChange.ts";
import ListLedgerChangePopover from "./ListLedgerChangePopover.tsx";

type PendingLedgerChange = {
  ledger: string;
  ledgerCaption: string;
  removedItems: string[];
};

const AUTO_LEDGER_CAPTION = "Auto (based on selected fields)";

const ListLedgerSelector = () => {
  const dispatch = useAppDispatch();
  const ledgers = useSelector(selectLedgers);
  const ledger = useSelector(selectListLedger);
  const listFieldsState = useSelector(selectListFieldsState);
  const editDisabled = useSelector(selectEditDisabled);
  const [selectorElement, setSelectorElement] = useState<HTMLDivElement | null>(null);
  const [pendingChange, setPendingChange] = useState<PendingLedgerChange>();

  const handleChange = (event: SelectChangeEvent) => {
    const nextLedger = event.target.value || undefined;
    const change = createListLedgerChange(listFieldsState, nextLedger);
    if (change.removedItems.length > 0 && nextLedger) {
      setPendingChange({
        ledger: nextLedger,
        ledgerCaption: ledgers.find((item) => item.name === nextLedger)?.caption ?? nextLedger,
        removedItems: change.removedItems,
      });
      return;
    }
    dispatch(fieldsStateActions.changeLedger(nextLedger));
  };

  return (
    <Stack spacing={0.75} ref={setSelectorElement}>
      <Typography variant="subtitle2">Ledger Source</Typography>
      <Select
        aria-label="Ledger"
        displayEmpty
        fullWidth
        value={ledger ?? ""}
        disabled={editDisabled}
        onChange={handleChange}
        renderValue={(value) => ledgers.find((item) => item.name === value)?.caption ?? AUTO_LEDGER_CAPTION}
        sx={{ ".MuiSelect-select": { py: 1 } }}
      >
        <MenuItem value="">{AUTO_LEDGER_CAPTION}</MenuItem>
        <Divider />
        {ledgers.map((item) => (
          <MenuItem key={item.name} value={item.name}>
            {item.caption}
          </MenuItem>
        ))}
      </Select>
      {pendingChange && (
        <ListLedgerChangePopover
          anchorEl={selectorElement ?? undefined}
          ledgerCaption={pendingChange.ledgerCaption}
          removedItems={pendingChange.removedItems}
          onCancel={() => setPendingChange(undefined)}
          onConfirm={() => {
            dispatch(fieldsStateActions.changeLedger(pendingChange.ledger));
            setPendingChange(undefined);
          }}
        />
      )}
    </Stack>
  );
};

export default ListLedgerSelector;
